import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import Login from './components/Login';
import MainApp from './MainApp';
import AppEditorPage from './pages/AppEditorPage';

function RequireAuth({ isLoggedIn, children }) {
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));

  const clearLocalAuth = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common.Authorization;
    setIsLoggedIn(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      if (localStorage.getItem('token')) await axios.post('/api/logout');
    } catch (err) {
      console.warn('[Auth] Failed to revoke session:', err?.message || err);
    } finally {
      clearLocalAuth();
    }
  }, [clearLocalAuth]);

  useEffect(() => {
    const syncAuthAcrossTabs = (event) => {
      if (event.key !== 'token') return;

      if (event.newValue) {
        axios.defaults.headers.common.Authorization = `Bearer ${event.newValue}`;
        setIsLoggedIn(true);
      } else {
        delete axios.defaults.headers.common.Authorization;
        setIsLoggedIn(false);
      }
    };

    window.addEventListener('storage', syncAuthAcrossTabs);
    return () => window.removeEventListener('storage', syncAuthAcrossTabs);
  }, []);

  const loginView = useMemo(
    () => (
      <div className="auth-page">
        <div className="container auth-container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-7 col-lg-5 col-xl-4">
              <Login onLogin={() => setIsLoggedIn(true)} />
            </div>
          </div>
        </div>
      </div>
    ),
    []
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={isLoggedIn ? <Navigate to="/" replace /> : loginView}
        />

        <Route
          path="/"
          element={
            <RequireAuth isLoggedIn={isLoggedIn}>
              <MainApp onLogout={logout} />
            </RequireAuth>
          }
        />

        <Route
          path="/:platform/apps/new"
          element={
            <RequireAuth isLoggedIn={isLoggedIn}>
              <AppEditorPage mode="create" />
            </RequireAuth>
          }
        />

        <Route
          path="/:platform/apps/:appId/edit"
          element={
            <RequireAuth isLoggedIn={isLoggedIn}>
              <AppEditorPage mode="edit" />
            </RequireAuth>
          }
        />

        <Route path="*" element={<Navigate to={isLoggedIn ? '/' : '/login'} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
